import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { fmtDate, fmtDateTime, money, currencySymbol, todayISO, localeDate } from '../../utils/region';
import { ExpenseBankLine, ExpenseKind, ExpenseStatus, FirmExpense } from '../../types';
import Pagination, { getPageSize } from '../Pagination';
import Fab from '../Fab';
import { useBadgeOnly } from '../BadgeFilter';
import { InvoiceList, QuotationList, ReceiptList } from '../Peak';
import OutboxView from '../Outbox';
import { tabColor, rowTint, menuPill, lighten, darken, pageLine, pageTint, pageToolbar, PAGE_INSET } from '../../tabPalette';
import { pageHref, openInNewTab } from '../../services/newTab';   // a document bubble's own address and right-click
import { displayClientName } from '../../utils/displayName';
import { brandOr } from '../../utils/brand';

// ── The firm's own money (Calem, reports 362b5a61 and eb29e199) ──────────────────────────────────
// Report 362b5a61 built this as four pages behind four top-menu buttons: "Build an 'Expense' section
// (4 bubbles) in the top menu and make it light/medium red color … include the following pages: Expense
// [office supplies, utilities, rent] · Equipment (machinery, computers, vehicles) · Asset [vehicle, land,
// long-term software etc] · Inventory [asset to expense, upon sale]", each with its own explanation on hover.
// Report eb29e199 replaced those four buttons with ONE reading "Accounting" and moved the classes onto this
// page: "Then, underneath that, do a second bubble bar running horizontally across the page that includes
// five categories in this order: Income/revenue … Expenses … Assets … Liabilities … Equity", in his colours,
// and "the 5-menu bubble can be visible all the time under the Accounting page". Equipment and Inventory are
// gone — "Equipment and inventory can move under Assets" — and everything that was recorded on them is on
// Assets now (server/src/db/index.ts). Income/Revenue, Liabilities and Equity open empty: "Income, revenue,
// liabilities, and equity should each open a new empty list with the same six columns."
// All five classes are THIS one list, told apart by `kind`: the reporter gave them the same six columns —
// "Doc. No. · Seller · Issue Date · Net Amount · Status · Activity" — and the same three ways in.
// Status is Draft / Pending / Approved / Denied, and "Only admin can change (to approved or denied)":
// the Approve / Deny buttons are drawn for an admin alone and the server allows them to admins alone.
// A row arrives by the round "+" at the bottom right, by uploading a supplier's PDF, or by picking a
// money-out line off a bank statement. Rows page 100 at a time, are soft-deleted, and carry the audit
// pair the Activity column shows.
// The page's list wears the open class's own colour — Revenue blue, Expenses the Accounting box's red,
// Assets green, Liabilities orange, Equity pink (src/tabPalette.ts: ACCOUNTING_BOX_OF; Revenue and Assets
// swapped at report 790f8217) — exactly as Petty Cash wears its coral. Each is asked for by the class, so
// the rows, the cards' edges and the From PDF / From Bank pills repaint together when a bubble is clicked.
// The two things that belong to the PAGE and not to a class — the corner "+" and the search box — keep the
// one top-menu colour instead, the "Accounting" button's red (PAGE_TAB below, Calem's report 7dab1f21).

// The bar Calem asked for, in his order. The same five, in the same order, as the module catalog's
// EXPENSE_MODULES (src/modules.ts) — typed by the class here so no screen can name a sixth.
const CLASSES: { key: ExpenseKind; label: string; tip?: string }[] = [
  { key: 'income', label: 'fe_cls_income' },
  { key: 'expense', label: 'fe_cls_expense', tip: 'fe_tip_expense' },
  { key: 'asset', label: 'fe_cls_asset', tip: 'fe_tip_asset' },
  { key: 'liability', label: 'fe_cls_liability' },
  { key: 'equity', label: 'fe_cls_equity' },
];

// ── The three documents under Revenue (report 790f8217) ──────────────────────────────────────────
// "Put quotations/invoices/receipts as above blue bubbles under "Income" (Accounting)", the screenshot's arrow running
// from the top menu's blue box to that bubble: they left the box for a row of bubbles under this class. Each key is
// still the Portal tab it always was, and ?tab=, ?open=, ?badge=1, the bell, a saved Default page and a right-click's
// new tab all read it, so the address says which one is open (ExpenseView's doc / onDoc). A bubble draws the very list
// the tab drew (components/Peak), under the same `peak` permission, with its own search and its own "+".
// Revenue's own list stays this row's home, as the Expenses home does for its row: clicking the open bubble, or
// "Revenue" itself, comes back to it.
// 2nd opening: "Center the quotation, invoice, and receipt bubbles and include the approval bubble as a pop-up when
// required." So the row now stands centred on the page instead of hanging from the Revenue bubble, and
// Approvals, the last of the blue box's four, joins it as a fourth bubble while there is something to approve. That
// "when" is the rule its top-menu button always went by (Portal: canSeeTab, handed down as showApprovals). It stays a
// Portal tab too, ?tab=outbox, the address "New approvals have arrived" opens (components/ApprovalsArrived). It is kept
// out of REVENUE_DOCS, which is also what a kept Default page is named from (Portal: savedDoc): Approvals is no page to
// keep as a default. Its list is OutboxView, which has a refresh of its own and nothing to add with a "+".
export type RevenueDoc = 'quotation' | 'invoice' | 'receipt';
const REVENUE_KIND: ExpenseKind = 'income';
export const REVENUE_DOCS: { key: RevenueDoc; label: string }[] = [
  { key: 'quotation', label: 'pk_quotation' },
  { key: 'invoice', label: 'pk_invoice' },
  { key: 'receipt', label: 'pk_receipt' },
];
const isRevenueDoc = (k: string): k is RevenueDoc => REVENUE_DOCS.some(d => d.key === k);
export type RevenuePage = RevenueDoc | 'outbox';
const APPROVALS_PAGE: { key: RevenuePage; label: string } = { key: 'outbox', label: 'mod_outbox' };
export const isRevenuePage = (k: string): k is RevenuePage => k === APPROVALS_PAGE.key || isRevenueDoc(k);

// ── The Expenses class's 15 sub-categories (Calem, report 35e85aca) ──────────────────────────────
// "When you click on Expenses, directly below it add the following 15 bubbles, also in the same red
// color as Expenses, but remove the outer gray bubble": COGS · Direct · Employee · Rent / Utilities ·
// Administrative · Professional · Software · Phone / Internet · Travel · Marketing · Repairs /
// Maintenance · Finance · Depreciation · General · Income Tax, his order and his words. Clicking one
// shows that sub-category's rows alone, and the Expenses page itself shows the rest: "If the user
// uploads a bunch of expenses that are yet to be categorized, leave those expenses on the home category
// 'expense' page until they have been assigned a subcategory and then they can disappear from the
// 'expense' category page directly and be filed under the correct subcategory instead. Once the user has
// successfully removed all uncategorized expenses, bring up a message on the main screen." His own words
// for that message were re-worded at report 079721fa and the screen now reads "Nothing left to
// Categorize... Great Work!" — the fe_cat_all_done row in src/i18n/index.ts is what it says, in three
// languages, and no comment here quotes it.
// The `key` is what is stored and filtered by (server/src/routes/firmExpenses.ts holds the same 15, and
// each row's own is one app_settings value — no column on firm_expenses), so the three languages can be
// re-worded without orphaning a row. Assets has ten of its own, Liabilities eleven and Equity five (ASSET_GROUPS,
// LIABILITY_GROUPS and EQUITY_CATEGORIES below); Revenue has none, so no picker on its form, and its list is never
// filtered by one.
const EXPENSE_KIND: ExpenseKind = 'expense';
const CATEGORIES: { key: string; label: string }[] = [
  { key: 'cogs', label: 'fe_cat_cogs' },
  { key: 'direct', label: 'fe_cat_direct' },
  { key: 'employee', label: 'fe_cat_employee' },
  { key: 'rent', label: 'fe_cat_rent' },
  { key: 'administrative', label: 'fe_cat_administrative' },
  { key: 'professional', label: 'fe_cat_professional' },
  { key: 'software', label: 'fe_cat_software' },
  { key: 'phone', label: 'fe_cat_phone' },
  { key: 'travel', label: 'fe_cat_travel' },
  { key: 'marketing', label: 'fe_cat_marketing' },
  { key: 'repairs', label: 'fe_cat_repairs' },
  { key: 'finance', label: 'fe_cat_finance' },
  { key: 'depreciation', label: 'fe_cat_depreciation' },
  { key: 'general', label: 'fe_cat_general' },
  { key: 'income_tax', label: 'fe_cat_income_tax' },
];

// ── The Assets class's 10 sub-categories, in two groups (report 21fa9aba) ────────────────────────
// "Under the Assets bubble of Accounting put the following sub-categories: Plant / Equipment Investments Intangibles
// above (non current) are lighter green Cash on Hand Cash In Bank Prepayments Receivables Inventories Tax Receivables
// Other above (current) are darker green then, put an outer stiched bubble around the first three and label them "Non
// Current" (non-current can be shown on the left of the screen) then, put an outer stiched bubble around the next seven
// label them "Current" (current can be shown on the right of the screen)". The reporter's order and words, stored and
// filtered by the slug exactly as the Expenses fifteen are (server/src/routes/firmExpenses.ts holds the same ten).
// As on Expenses, the Assets page itself lists only the rows nobody has filed yet, and a filed row moves under its
// bubble. Nothing sorts a row by itself: every row Assets already held, the old Equipment and Inventory ones included,
// waits on that home until someone picks a sub-category on its form.
// Both greens are asked for from the Assets box (src/tabPalette.ts). The lighter is that box's own pill: the pale wash
// with green words, solid green when open. The box has no darker pill to borrow, so Current's is worked out here from
// the same solid green: 3.25 shades lighter with half-way-to-black words and a solid line, a shade darker when open.
// 2nd opening: "Bring all assets back to back in the center of the page and take the word "current" and put it to the
// right side of the group." The screenshot's arrows point the two stitched bubbles at each other, with "Current"
// circled and a line drawn from it to the far end of its group. That replaces the left and right of the screen quoted
// above: the two stand side by side in the middle of the page (groupRow, ExpenseView) and Current's name comes after
// its seven pills (captionEnd). Non Current keeps its name in front of its three: the reporter named only "current".
// Report 54ffdf28: "Change the bubble background color to one shade lighter." The screenshot is Current's own pills,
// unopened, so that fill alone goes from two shades lighter to three, #8ECFBA to #C7E7DD. Their line, their words and
// the open pill stay, and Non Current keeps its paler #E1F5EE, so the lighter and darker greens above stay distinct.
// Its 2nd opening: "Make the current asset bubble background another shade lighter and create more separation (15px)
// between non-current and current." That fill alone goes a quarter of a shade on, to #D5EDE5: a whole shade on is pure
// white and a half, #E3F3EE, is all but Non Current's own, either of which would lose the two greens above. The next
// step back is #C7E7DD. Both screenshots with this opening still show the old #8ECFBA, from a stale tab or from before
// #C7E7DD went live, so neither of them judges it. The 15px was ASSET_GROUP_GAP, since raised by report 18362c07.
const ASSET_KIND: ExpenseKind = 'asset';
// `words` takes the place of the half-way-to-black words on an unopened pill; only Liabilities' Current passes it.
const currentPill = (kind: ExpenseKind, words?: string) => (active: boolean): React.CSSProperties => {
  const solid = tabColor(kind)!;
  return active
    ? { borderColor: darken(solid, 1), background: darken(solid, 1), color: '#fff' }
    : { borderColor: solid, background: lighten(solid, 3.25), color: words || darken(solid, 2) };
};
interface CatGroup {
  key: string; label: string; pill: (active: boolean) => React.CSSProperties; captionEnd?: boolean;
  cats: { key: string; label: string }[];
}
const ASSET_GROUPS: CatGroup[] = [
  {
    key: 'noncurrent', label: 'fe_cat_group_noncurrent', pill: active => menuPill(ASSET_KIND, active),
    cats: [
      { key: 'plant_equipment', label: 'fe_cat_plant_equipment' },
      { key: 'investments', label: 'fe_cat_investments' },
      { key: 'intangibles', label: 'fe_cat_intangibles' },
    ],
  },
  {
    key: 'current', label: 'fe_cat_group_current', pill: currentPill(ASSET_KIND), captionEnd: true,
    cats: [
      { key: 'cash_on_hand', label: 'fe_cat_cash_on_hand' },
      { key: 'cash_in_bank', label: 'fe_cat_cash_in_bank' },
      { key: 'prepayments', label: 'fe_cat_prepayments' },
      { key: 'receivables', label: 'fe_cat_receivables' },
      { key: 'inventories', label: 'fe_cat_inventories' },
      { key: 'tax_receivables', label: 'fe_cat_tax_receivables' },
      { key: 'other', label: 'fe_cat_other' },
    ],
  },
];

// ── The Liabilities class's 11 sub-categories, in two groups (report 21fa9aba, 3rd opening) ──────
// "Under the liabilities bubble add the following categories: Long-term Loans Employee Benefits Other NC Liabilities",
// then "Call the above liabilities non-current and put them in a stitched bubble on the left of screen then on the
// right of the screen, but again back-to-back and centered on the screen, add the current liabilities listed below.
// Short-term Loans Payables Advances (Customers) VAT Paybles WHT Paybles SSO Payables CIT Payable Other Payables". The
// reporter's order and words, with "Paybles" spelled properly, stored and filtered by the slug exactly as the Assets
// ten are (server/src/routes/firmExpenses.ts holds the same eleven). The same rule holds too: every row Liabilities
// already held waits on its home until someone picks a sub-category on its form.
// Neither comment names a colour or a place for the groups' names, so all of that follows Assets: Non Current's name
// in front of its pills, in the box's own lighter pill; Current's name after its pills, in the darker pill worked out
// from this box's solid orange. English calls the two groups what Assets calls its own, but the Chinese and Thai names
// there say "assets", so these two have keys of their own.
// 4th opening: "Change them as follows to see if you can fit them on one line, centered." The screenshot strikes "NC",
// writes "Clients" over "Customers" and strikes "Payables" off VAT, WHT, SSO, CIT and Other. That replaces the words
// quoted above in the fe_cat_ rows of src/i18n/index.ts and nowhere else: the slugs below keep the old words, so a
// filed row stays under its bubble. It also circles Payables and draws a curved arrow from it to the far end of
// Advances, read here as a move: Payables now follows Advances, in this list and in the server's.
// Report 0a97c655's 2nd opening: "Change the following to red text but ensure that it's darker than its background."
// Its screenshot, the report's second, is Current's pills, unopened, whose half-way-to-black words come to #6C2D18 and
// read brown on their #F8E0D8 fill. So those words alone go to the Accounting top-menu button's red one shade darker,
// #A83E3E, about 4.9:1 on that fill (arithmetic, not seen on a page); the button's own red would be about 3:1. It is
// read from tabColor by that button's key, as PAGE_COLOR is declared further down and would not exist yet here. The
// fill, the coral line and the open pill's white on solid stay. So does the grey "Current" caption, which has no fill
// of its own, where the comment concerns text against its background. Non Current is not in the screenshot and Assets'
// words are not mentioned, so those keep theirs: the red is an argument to currentPill, not its default.
const LIABILITY_KIND: ExpenseKind = 'liability';
const LIABILITY_GROUPS: CatGroup[] = [
  {
    key: 'noncurrent', label: 'fe_cat_group_liab_noncurrent', pill: active => menuPill(LIABILITY_KIND, active),
    cats: [
      { key: 'long_term_loans', label: 'fe_cat_long_term_loans' },
      { key: 'employee_benefits', label: 'fe_cat_employee_benefits' },
      { key: 'other_nc_liabilities', label: 'fe_cat_other_nc_liabilities' },
    ],
  },
  {
    key: 'current', label: 'fe_cat_group_liab_current', pill: currentPill(LIABILITY_KIND, darken(tabColor('expense')!, 1)), captionEnd: true,
    cats: [
      { key: 'short_term_loans', label: 'fe_cat_short_term_loans' },
      { key: 'advances_customers', label: 'fe_cat_advances_customers' },
      { key: 'payables', label: 'fe_cat_payables' },
      { key: 'vat_payables', label: 'fe_cat_vat_payables' },
      { key: 'wht_payables', label: 'fe_cat_wht_payables' },
      { key: 'sso_payables', label: 'fe_cat_sso_payables' },
      { key: 'cit_payable', label: 'fe_cat_cit_payable' },
      { key: 'other_payables', label: 'fe_cat_other_payables' },
    ],
  },
];

// ── The Equity class's 5 sub-categories (report dc2a3df6) ────────────────────────────────────────
// "Add the following equity bubbles, also centered as per every other subcategory. Share Capital Retained Earnings
// Dividends Paid Current Year P / L Drawings". The reporter's order and words, stored and filtered by the slug exactly
// as the others are (server/src/routes/firmExpenses.ts holds the same five). The report names no groups, so these stand
// as the Expenses fifteen do: one plain centred row with no stitched bubble, in the pink of the Equity bubble above
// them (menuPill). The same rule holds too: every row Equity already held waits on its home until someone picks a
// sub-category on its form.
const EQUITY_CATEGORIES: { key: string; label: string }[] = [
  { key: 'share_capital', label: 'fe_cat_share_capital' },
  { key: 'retained_earnings', label: 'fe_cat_retained_earnings' },
  { key: 'dividends_paid', label: 'fe_cat_dividends_paid' },
  { key: 'current_year_pl', label: 'fe_cat_current_year_pl' },
  { key: 'drawings', label: 'fe_cat_drawings' },
];
// The classes whose sub-categories stand in stitched groups, read by the page's row and by the pop-up's Category box.
const GROUPS_OF: Partial<Record<ExpenseKind, CatGroup[]>> = { asset: ASSET_GROUPS, liability: LIABILITY_GROUPS };
// The classes whose sub-categories stand in one plain row with no group round them, read by the same two.
const PLAIN_CATS_OF: Partial<Record<ExpenseKind, { key: string; label: string }[]>> = {
  expense: CATEGORIES, equity: EQUITY_CATEGORIES,
};
/** The sub-categories a class has, in their order; none for Revenue, the one class without any. */
const categoriesOf = (k: ExpenseKind): { key: string; label: string }[] => (
  PLAIN_CATS_OF[k] || (GROUPS_OF[k] || []).flatMap(g => g.cats));

// ── The staff-cost detail of an Employee expense (Calem, report 079721fa) ────────────────────────
// "Add three columns (employee expense) in between seller and issue date … Description (choose from
// salary, bonus, training, benefits) · Employee (choose from all … employees) · Period (choose from
// month, quarter, half, full year)", and ONE sub-category carries them: "Only rows filed under the
// employee subcategory bubble." That is what keeps a rent or software bill out of it — the four
// descriptions are all wages, and three empty cells on every ordinary supplier bill would squeeze the
// Seller column for nothing. So the three columns are drawn while the Employee bubble is the open
// sub-category and only then, and the boxes are on the row's pop-up while its sub-category is
// Employee and only then.
// The Employee box offers the whole active staff list (api.getServiceUsers, the endpoint the Contacts
// card's and the Services module's own pickers read) — his answer when told that nothing on a login
// says which office it belongs to: "Just make it the entire staff list if you cannot choose … based
// specifically." The login's ID is what is stored, never a typed-in name, so a member of staff can be
// renamed without orphaning an expense. The slugs below are what the server keeps, so all three lists
// can be re-worded in any language without orphaning a row.
const STAFF_CATEGORY = 'employee';
const STAFF_DESCRIPTIONS: { key: string; label: string }[] = [
  { key: 'salary', label: 'fe_desc_salary' },
  { key: 'bonus', label: 'fe_desc_bonus' },
  { key: 'training', label: 'fe_desc_training' },
  { key: 'benefits', label: 'fe_desc_benefits' },
];
// ── The second round on the same three (Calem, report 079721fa) ─────────────────────────────────
// "Change issue date to payment date in the column below · Change employee names in the list to their
// full first name not their nicknames · Change the period as follows. Day = payment date · Month =
// select month · Quarter = Select a quarter, either four quarters of the financial/calendar year or
// quarter starting from the payment date · Half Year = … · Year = …" — and, on the names: "a separate
// 'Full name' box added to each staff record, left to the nickname elsewhere and used only on the
// Accounting lists; the person filing the expense picks which of the two on each row."
// So: Day joins the Period list (it asks nothing more — it IS the payment date), every other period
// asks a second question, and the Employee box is followed by a two-way pick of which word to show.
// The date column and the pop-up's date box are named "Payment Date" under this bubble and Issue Date
// everywhere else; the stored field is the one date either way.
const STAFF_PERIODS: { key: string; label: string }[] = [
  { key: 'day', label: 'fe_per_day' },
  { key: 'month', label: 'fe_per_month' },
  { key: 'quarter', label: 'fe_per_quarter' },
  { key: 'half_year', label: 'fe_per_half_year' },
  { key: 'full_year', label: 'fe_per_full_year' },
];
// Which of the employee's two words a row shows. '' = nobody has been asked yet, and such a row goes
// on showing the Name record exactly as it did before this box existed.
const STAFF_NAME_PICKS: { key: string; label: string }[] = [
  { key: 'full', label: 'fe_name_full' },
  { key: 'nickname', label: 'fe_name_nick' },
];

// ── The windows a Period offers ──────────────────────────────────────────────────────────────────
// The second question's answers are DATE WINDOWS, and they are drawn from two things the row and the
// firm already hold: the row's own payment date, and the day the firm's financial year starts (the
// org default an admin keeps, app_settings 'fy_start' — Jan 1 by default, which makes these the
// calendar quarters, halves and year). No year, quarter or month is written into the code, and a row
// re-reads its window if its payment date is corrected or the financial year is changed in Admin.
// What is STORED against the row is the slug alone: 'm1'…'m12' for a month of the payment date's
// year, 'fy1'…'fy4' for the fixed windows of the financial year, and 'rolling' for the one that
// starts on the payment date itself. server/src/routes/firmExpenses.ts holds the same list.
interface FyStart { month: number; day: number }
const FY_CALENDAR: FyStart = { month: 1, day: 1 };   // until the list answers with the firm's own
const pad2 = (n: number) => String(n).padStart(2, '0');
const lastDayOf = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
/** A calendar date, the day clamped into its month (a 31st in a 30-day month is the 30th). */
const isoOf = (y: number, m: number, d: number) => `${y}-${pad2(m)}-${pad2(Math.min(d, lastDayOf(y, m)))}`;
const addMonthsIso = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1 + n, 1));
  return isoOf(t.getUTCFullYear(), t.getUTCMonth() + 1, d);
};
const addDaysIso = (iso: string, n: number): string => {
  const t = new Date(`${iso}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};
/** The start of the financial year the payment date falls in — this year's turn of it, or last year's. */
const fyStartOn = (paid: string, fy: FyStart): string => {
  const y = Number(paid.slice(0, 4));
  const here = isoOf(y, fy.month, fy.day);
  return here <= paid ? here : isoOf(y - 1, fy.month, fy.day);
};
interface PeriodWindow { key: string; start: string; end: string; rolling: boolean }
/** Every window the chosen period offers, in Calem's own order: the fixed ones, then the rolling one. */
function periodWindows(period: string, paid: string, fy: FyStart): PeriodWindow[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(paid) || !period || period === 'day') return [];
  if (period === 'month') {
    const y = Number(paid.slice(0, 4));
    return Array.from({ length: 12 }, (_, i) => ({
      key: `m${i + 1}`, start: isoOf(y, i + 1, 1), end: isoOf(y, i + 1, lastDayOf(y, i + 1)), rolling: false,
    }));
  }
  const span = period === 'quarter' ? 3 : period === 'half_year' ? 6 : period === 'full_year' ? 12 : 0;
  if (!span) return [];
  const first = fyStartOn(paid, fy);
  const out: PeriodWindow[] = [];
  for (let i = 0; i < 12 / span; i++) {
    const start = addMonthsIso(first, i * span);
    out.push({ key: `fy${i + 1}`, start, end: addDaysIso(addMonthsIso(start, span), -1), rolling: false });
  }
  out.push({ key: 'rolling', start: paid, end: addDaysIso(addMonthsIso(paid, span), -1), rolling: true });
  return out;
}
/** A month window reads as its own month ("March 2026"); every other window reads as its two dates. */
const monthName = (iso: string): string => {
  try { return new Intl.DateTimeFormat(localeDate(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`)); }
  catch { return fmtDate(iso); }
};
/** One window's own words. The pop-up's second box and the list's Period column read this same
 *  function, which is what makes a cell say exactly what was picked in the box. */
const windowLabel = (period: string, w: PeriodWindow, t: (k: string) => string): string => {
  const span = period === 'month' ? monthName(w.start) : `${fmtDate(w.start)} – ${fmtDate(w.end)}`;
  return w.rolling ? `${t('fe_per_from_payment')} · ${span}` : span;
};
// ── What the Period column says (Calem, report 079721fa) ─────────────────────────────────────────
// "The period column from the info selected in the detail" — the answer was being stored and asked
// for in the pop-up but no column showed it, so the only way to read a row's period was to open it.
// The cell words what is stored, never a second wording of its own: the window is re-measured off
// the row's payment date and the firm's financial year (periodWindows, the same call the pop-up's
// list is drawn from) and then read out by windowLabel above, so a month reads "March 2026", a
// quarter / half / year reads its two dates and the rolling one keeps its "from the payment date".
// Day has no window to name — it IS the payment date — so it reads that date. A row whose period
// was picked but whose window never was, and one saved before the second question existed, falls
// back to the period's own word; a row with no period at all reads the dash every empty cell does.
const periodText = (r: FirmExpense, fy: FyStart, t: (k: string) => string): string => {
  const period = r.staff_period || '';
  if (!period) return '—';
  if (period === 'day') return fmtDate(r.issue_date);
  const w = periodWindows(period, r.issue_date, fy).find(x => x.key === r.staff_period_pick);
  return w ? windowLabel(period, w, t) : t(STAFF_PERIODS.find(p => p.key === period)?.label || 'fe_col_period');
};
// The three columns' own widths: one of four short words, a person's name at the 170 the Activity
// column stands at, and the period's own words at that same 170 — two dates, or a month and its year,
// with the rolling one's prefix wrapping onto a second line as the Employee column's long names do.
// They are only drawn under the Employee bubble, so Seller only shares with them there.
const STAFF_DESC_W = 130;
const STAFF_EMP_W = 170;
const STAFF_PER_W = 170;
// ── The columns, left to right (Calem, report 079721fa) ─────────────────────────────────────────
// "remove the first row (doc number). Then you'll start with columns - seller/description/employee/
// issue date/net amount/status/activity … doc number is not required for any expense." So the Doc. No.
// column and its box on the pop-up are gone from all five account classes. The stored doc_no is NOT:
// a supplier PDF still has its number read into it and a bank line still brings its own across, and the
// search still matches them — only nothing shows or edits it now.
// Seller is the column with no width of its own, so dropping Doc. No.'s 190px out of the colgroup is the
// whole of the re-sharing: the width lands on Seller because the colgroup gives it to no one else, and no
// th carries a calc() of its own (report bb719e6b). TABLE_MIN is the old 900 less that same 190.
const TABLE_MIN = 710;

// The open class's colours. The non-null marks are safe: every class of CLASSES has a box behind it
// (ACCOUNTING_BOX_OF), which is what tabColor / rowTint / pageTint read.
const clsColor = (k: ExpenseKind) => tabColor(k)!;
const clsRowBg = (k: ExpenseKind) => rowTint(k, 1)!;
const clsLine = (k: ExpenseKind) => `1px solid ${pageLine(k)}`;
// ── The page's OWN colour, the one its top menu wears (Calem, report 7dab1f21) ───────────────────
// "The + ADD icon in the bottom right corner of the page and the Search All should be the same as the top
// menu color." The page stands behind a single top-menu button, "Accounting", whose Portal tab key is still
// `expense` (src/tabPalette.ts: TAB_GROUP_OF) — so that button's red is tabColor('expense'), and asking by
// the key is what keeps it the button's colour if the colour is ever changed there again.
// Those two are the page's furniture, not a class's: with Equity open they were coming out pink against a
// red menu button. They wear this whichever bubble is open, the corner "+" as every other page's does
// (Bugify #7568b3b5). The five bubbles, the rows, the cards' edges and the From PDF / From Bank pills are
// the open class's and go on repainting with it, as report eb29e199 asked.
const PAGE_TAB: ExpenseKind = 'expense';
const PAGE_COLOR = clsColor(PAGE_TAB);
// The search box's border, wash and words — an unselected pill of that one box with its wash a shade deeper,
// exactly the dressing it had, only asked for by the page instead of by the open class.
const PAGE_SEARCH: React.CSSProperties = { ...menuPill(PAGE_TAB, false), background: lighten(pageTint(PAGE_TAB)!, -1) };
const CARD_R = 10;
const CARD_GAP = 7;
// The page gutter, the one every screen in the system now stands in by: PAGE_INSET's 15, where this list had its own
// 17 (Calem, report 76a2c198, 5th opening — see PAGE_INSET in tabPalette). The cards, the table and the toolbar
// bubble all read it here, so they move together and the header's edge strips still cover the padding.
const CARD_INSET = PAGE_INSET;
// ── Every bubble on this page: the FINAL spec (report 0d7b170d) ─────────────────────────────────
// Reports eb29e199, 67a982e2, 35e85aca and 775719f3 gave these bubbles rules of their own — a flex share of the bar
// for each class bubble and for each gap, a 12.5px/700 word on 14px sides, a 6px gap on the sub-category row — and
// then nudged them a few pixels at a time, opening after opening. Report 0d7b170d settled it: "Change the bubbles in
// the Accounting category to be the same as all other bubbles in top bars. In both size and spacing … each
// subcategory is ultimately centered underneath the top accounting category". So the five class bubbles, the 15
// sub-category bubbles and the Upload PDF / from Bank Statement pair are all BAR_PILL — the Petty Cash and Bank
// Statement top bars' pill: 5px 12px, 12px/600, round ends — each at its own natural width, with BAR_GAP, the 8px
// every top bar keeps, between neighbours in both rows. Only the colours are this page's own (menuPill).
// This is LOCKED. No report may make a bubble here bigger or smaller, or a gap wider or tighter, for this page alone:
// a change to the pill is a change to every top bar at once. Only "centered underneath" has moved on since: report
// 790f8217's 3rd opening centres the sub-category row on the page, for the reason given at that row (ExpenseView).
// ASSET_GROUP_GAP below leaves this alone: it is the gap between a row's two stitched groups, not a pill gap.
const BAR_GAP = 8;
const BAR_PILL: React.CSSProperties = {
  flex: 'none', padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid',
  whiteSpace: 'nowrap', cursor: 'pointer',
};
// The plain word that names a run of bubbles: the class bar's "Categories" (report 9a24f8c1) and each stitched bubble's
// on the Assets and Liabilities rows (report 21fa9aba). It is not a pill: no border, no fill, nothing happens when
// it is clicked.
const BAR_CAPTION: React.CSSProperties = { flex: 'none', fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
// The clear over the sub-category row (report 35e85aca, 3rd opening: "add 15 pixels of space between the bubble above
// and the categories below"): 15px on top of the 7px of page the class bar leaves under itself (TOOLBAR_GAP), 22px.
const CAT_ROW_DROP = 15;
// The clear between the two stitched groups of the Assets row and of the Liabilities row, dashed line to dashed line:
// 15px at report 54ffdf28's 2nd opening ("create more separation (15px) between non-current and current"), 25px since
// report 18362c07 ("Increase the gap between current and non-current by 10 pixels Make it the same gap for current and
// non-current liabilities"). Both rows read this one number. It holds across only: once each group has a line of its
// own on a narrow window, BAR_GAP still stands between those lines, as it does between the pills in each group.
const ASSET_GROUP_GAP = 25;

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };

// The status word, coloured — the amber / green / red the rest of the system already uses for
// waiting / agreed / refused (the Due Diligence list's status chips), with Draft the plain grey of
// something nobody has acted on yet.
const STATUS_COLOR: Record<ExpenseStatus, string> = {
  draft: 'var(--text-tertiary)', pending: '#B45309', approved: '#15803D', denied: '#B42318',
};
const STATUS_LABEL: Record<ExpenseStatus, string> = {
  draft: 'fe_st_draft', pending: 'fe_st_pending', approved: 'fe_st_approved', denied: 'fe_st_denied',
};
const statusOf = (v: any): ExpenseStatus =>
  (['draft', 'pending', 'approved', 'denied'].includes(String(v)) ? (String(v) as ExpenseStatus) : 'draft');

// ── The entry / edit form ────────────────────────────────────────────────────────────────────────
function ExpenseForm({ kind, row, fy, onClose, onSaved }: {
  kind: ExpenseKind; row: FirmExpense | null; fy: FyStart; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isAdmin = useStore(s => s.me?.role === 'admin');
  const color = clsColor(kind);
  const isNew = !row;
  const [seller, setSeller] = useState(row?.seller || '');
  const [issueDate, setIssueDate] = useState(row?.issue_date || todayISO());
  const [netAmount, setNetAmount] = useState(row ? String(row.net_amount ?? '') : '');
  const [status, setStatus] = useState<ExpenseStatus>(statusOf(row?.status));
  // The sub-category (reports 35e85aca, 21fa9aba): '' while the row is still waiting on its class's home.
  const [category, setCategory] = useState(row?.category || '');
  // The staff-cost detail (report 079721fa), on screen while the sub-category picked above is Employee.
  const [staffDesc, setStaffDesc] = useState(row?.staff_description || '');
  const [staffEmp, setStaffEmp] = useState(row?.staff_employee || '');
  const [staffPeriod, setStaffPeriod] = useState(row?.staff_period || '');
  // Which of the employee's two words this row shows, and WHICH month / quarter / half / year the
  // period names ('' on a Day period, and on a row filed before the second question existed).
  const [staffNameAs, setStaffNameAs] = useState(row?.staff_name_as || '');
  const [staffPick, setStaffPick] = useState(row?.staff_period_pick || '');
  // The whole active staff list, from the endpoint the Contacts card's own pickers read. Asked for once
  // the form opens on the Expenses class, which is the only class with an Employee sub-category.
  const [staffUsers, setStaffUsers] = useState<{ id: string; name: string; email: string; nickname?: string; full_name?: string }[]>([]);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const staffFields = kind === EXPENSE_KIND && category === STAFF_CATEGORY;
  useEffect(() => {
    if (kind !== EXPENSE_KIND) return;
    api.getServiceUsers().then((d: any) => setStaffUsers(Array.isArray(d) ? d : (d?.rows || []))).catch(() => { });
  }, [kind]);
  // What a member of staff is called on THIS row, the same three ways the list resolves it: the Full
  // name an admin recorded in Admin → Users, what colleagues call them, or — until the filer has been
  // asked — the Name record this column has always shown. Each falls back to the Name record and then
  // the address, so neither the box nor the column is ever left blank.
  const staffLabel = (u: { name?: string; email: string; nickname?: string; full_name?: string }): string =>
    (staffNameAs === 'full' && u.full_name ? u.full_name
      : staffNameAs === 'nickname' ? (u.nickname || u.name || u.email)
        : (u.name || u.email));
  // The windows the chosen period offers, measured off this row's payment date and the firm's
  // financial year. Day offers none — it is the payment date itself — and nor does an empty period.
  const periodChoices = periodWindows(staffPeriod, issueDate, fy);
  // Changing the period changes the list, so a window that the new one does not offer is let go of
  // rather than carried under a heading it does not belong to (the server does the same on save).
  const pickPeriod = (p: string) => {
    setStaffPeriod(p);
    setStaffPick(cur => (periodWindows(p, issueDate, fy).some(w => w.key === cur) ? cur : ''));
  };
  // The second box is headed by the period it belongs to — "Quarter", "Month" — so the two read as
  // one question. Its own word is the one on the Period list above it.
  const periodWord = STAFF_PERIODS.find(p => p.key === staffPeriod)?.label || 'fe_col_period';

  // Status rides along only while it is the operator's own half, Draft or Pending. An expense an admin
  // has already Approved or Denied keeps that decision through an ordinary edit — the server refuses a
  // decision sent this way, so sending it back unchanged would make Save fail on those rows.
  // The sub-category rides along for the classes that have them, by the same form whichever way the row
  // arrived — typed in, read off a supplier PDF or picked off a bank statement.
  // The three staff boxes ride along only while they are on screen, so filing an Employee expense under
  // some other sub-category leaves the detail it already carries alone instead of emptying it.
  // doc_no is not sent at all any more (report 079721fa): the server keeps whatever the row holds, so a
  // number read off a supplier PDF or brought across from a bank line survives every edit here.
  const body = () => ({
    seller: seller.trim(), issue_date: issueDate, net_amount: Number(netAmount) || 0,
    ...(status === 'draft' || status === 'pending' ? { status } : {}),
    ...(categoriesOf(kind).length ? { category } : {}),
    ...(staffFields ? {
      staff_description: staffDesc, staff_employee: staffEmp, staff_period: staffPeriod,
      staff_name_as: staffNameAs, staff_period_pick: staffPick,
    } : {}),
  });
  const save = async () => {
    if (!seller.trim()) return setErr(t('fe_need_seller'));
    setSaving(true); setErr('');
    try {
      if (isNew) await api.createFirmExpense(kind, body());
      else await api.updateFirmExpense(kind, row!.id, body());
      onSaved(); onClose();
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };
  // Approve / Deny: drawn for an admin only, and refused by the server for anyone else.
  const decide = async (to: 'approved' | 'denied') => {
    if (!row) return;
    setSaving(true); setErr('');
    try { await api.decideFirmExpense(kind, row.id, to); onSaved(); onClose(); }
    catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!row || !window.confirm(t('fe_delete_confirm'))) return;
    setSaving(true); setErr('');
    try { await api.deleteFirmExpense(kind, row.id); onSaved(); onClose(); }
    catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const field = (label: string, value: string, set: (v: string) => void, extra?: React.CSSProperties, type = 'text') => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input value={value} onChange={e => set(e.target.value)} type={type} style={{ ...inputStyle, ...extra }} />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
          {isNew ? t('fe_add', { page: t(`mod_${kind}`) }) : t('edit')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* The Doc. No. box is gone with report 079721fa — "doc number is not required for any
              expense" — for every account class, and the three staff boxes below stand in its place
              ("add these fields to the pop-up rather than doc number"). */}
          {field(t('fe_col_seller'), seller, setSeller)}
          {/* The one stored date, named for what it is on a staff cost — "Change issue date to
              payment date" (report 079721fa). The word follows the Category box below: pick
              Employee and it reads Payment Date, pick anything else and it is the Issue Date
              every other class knows. */}
          {field(t(staffFields ? 'fe_col_payment_date' : 'fe_col_issue_date'), issueDate, setIssueDate, undefined, 'date')}
          <div>
            <label style={labelStyle}>{t('fe_col_net_amount')}</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-tertiary)' }}>{currencySymbol()}</span>
              <input value={netAmount} onChange={e => setNetAmount(e.target.value)} inputMode="decimal" style={{ ...inputStyle, paddingLeft: 24 }} />
            </div>
          </div>
          {/* Draft ⇄ Pending is anyone's; Approved / Denied is the two buttons below, admins only.
              The sub-category stands beside it (report 35e85aca) and is this one box for every expense
              however it arrived, so a batch of uploaded or imported Drafts is filed — and re-filed — the
              same way. Picking one here is what takes the row off its class's home and puts it under its
              bubble; the blank option brings it back. Assets and Liabilities have the same box with theirs under
              their two group names (report 21fa9aba), and Equity with its five (report dc2a3df6). Revenue has no
              such box. */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>{t('fe_col_status')}</label>
              {status === 'approved' || status === 'denied' ? (
                <div style={{ fontSize: 13, fontWeight: 700, color: STATUS_COLOR[status] }}>{t(STATUS_LABEL[status])}</div>
              ) : (
                <div style={{ display: 'flex', gap: 10 }}>
                  {(['draft', 'pending'] as const).map(k => (
                    <button key={k} type="button" onClick={() => setStatus(k)}
                      style={{ flex: 1, padding: '9px 12px', borderRadius: 999, border: '1px solid', fontSize: 13, fontWeight: 700, cursor: 'pointer', ...menuPill(kind, status === k) }}>
                      {t(STATUS_LABEL[k])}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {categoriesOf(kind).length > 0 && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={labelStyle}>{t('fe_cat_label')}</label>
                <select value={category} onChange={e => setCategory(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', color: category ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                  <option value="">{t('fe_cat_none')}</option>
                  {GROUPS_OF[kind] ? GROUPS_OF[kind].map(g => (
                    <optgroup key={g.key} label={t(g.label)}>
                      {g.cats.map(c => <option key={c.key} value={c.key}>{t(c.label)}</option>)}
                    </optgroup>
                  )) : categoriesOf(kind).map(c => <option key={c.key} value={c.key}>{t(c.label)}</option>)}
                </select>
              </div>
            )}
          </div>
          {/* The staff-cost detail (report 079721fa), under the sub-category box that governs it: it
              appears the moment "Employee" is the Category picked above and goes again if another
              sub-category is chosen. Plain selects, the Employee one offering the whole active
              staff list by name — a person is stored by their login, never by the name typed. Two
              rows since the second round of that report: what the cost is, who it was for and which
              of their two names to show, then the period and which one of it. Nothing here is
              required: an Employee expense can be saved with every box still blank. */}
          {staffFields && (
            <>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={labelStyle}>{t('fe_col_description')}</label>
                  <select value={staffDesc} onChange={e => setStaffDesc(e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer', color: staffDesc ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                    <option value="">—</option>
                    {STAFF_DESCRIPTIONS.map(d => <option key={d.key} value={d.key}>{t(d.label)}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={labelStyle}>{t('fe_col_employee')}</label>
                  <select value={staffEmp} onChange={e => setStaffEmp(e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer', color: staffEmp ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                    <option value="">—</option>
                    {/* The one already on the row, while the list is still on its way or no longer holds
                        them (a login since made inactive) — so an old expense keeps naming its person
                        instead of looking empty, and saving it again does not drop them. */}
                    {!!staffEmp && !staffUsers.some(u => u.id === staffEmp) && (
                      <option value={staffEmp}>{row?.staff_employee_name || '—'}</option>
                    )}
                    {staffUsers.map(u => <option key={u.id} value={u.id}>{staffLabel(u)}</option>)}
                  </select>
                </div>
                {/* Which of the two words this row shows for that person (report 079721fa): "the
                    person filing the expense picks which of the two on each row". It re-words the
                    box beside it as well as the Employee column on the list, and it changes nothing
                    about the staff record itself. Left at "—" the row shows the Name record, which
                    is what this column has always shown; a record with no Full name recorded shows
                    that same Name record even when Full name is picked. */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={labelStyle}>{t('fe_name_as')}</label>
                  <select value={staffNameAs} onChange={e => setStaffNameAs(e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer', color: staffNameAs ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                    <option value="">—</option>
                    {STAFF_NAME_PICKS.map(n => <option key={n.key} value={n.key}>{t(n.label)}</option>)}
                  </select>
                </div>
              </div>
              {/* The period, and then WHICH one (report 079721fa). Day asks nothing further — it is
                  the payment date — so the right-hand half simply stays empty and the Period box
                  does not change width as the answer changes. Every window is drawn from the
                  payment date above and the firm's financial year, so correcting either re-words
                  these options. */}
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={labelStyle}>{t('fe_col_period')}</label>
                  <select value={staffPeriod} onChange={e => pickPeriod(e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer', color: staffPeriod ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                    <option value="">—</option>
                    {STAFF_PERIODS.map(p => <option key={p.key} value={p.key}>{t(p.label)}</option>)}
                  </select>
                </div>
                {periodChoices.length > 0 ? (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={labelStyle}>{t(periodWord)}</label>
                    <select value={staffPick} onChange={e => setStaffPick(e.target.value)}
                      style={{ ...inputStyle, cursor: 'pointer', color: staffPick ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                      <option value="">—</option>
                      {periodChoices.map(w => <option key={w.key} value={w.key}>{windowLabel(staffPeriod, w, t)}</option>)}
                    </select>
                  </div>
                ) : <div aria-hidden style={{ flex: 1, minWidth: 0 }} />}
              </div>
            </>
          )}
          {row && !!row.file_name && (
            <button type="button" onClick={() => api.openFirmExpenseFile(kind, row.id).catch(e => setErr(e.message))}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textAlign: 'left', ...menuPill(kind, false) }}>
              <i className="ti ti-paperclip" style={{ fontSize: 15 }} aria-hidden />{row.file_name}
            </button>
          )}
          {row && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.7, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div>{t('fe_created')}: {row.created_by_name || '—'} · {fmtDateTime(row.created_at)}</div>
              <div>{t('fe_updated')}: {row.updated_by_name || '—'} · {fmtDateTime(row.updated_at)}</div>
              <div>{t('fe_source')}: {t(`fe_src_${row.source}`)}</div>
            </div>
          )}
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}
          {/* An admin's decision on an expense that is waiting. */}
          {row && isAdmin && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" disabled={saving} onClick={() => decide('approved')}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: STATUS_COLOR.approved, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {t('fe_approve')}
              </button>
              <button type="button" disabled={saving} onClick={() => decide('denied')}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: STATUS_COLOR.denied, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {t('fe_deny')}
              </button>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
            {row && (
              <button type="button" onClick={remove} style={{ padding: '9px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'transparent', color: '#E24B4A', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {t('delete')}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" onClick={onClose} style={{ padding: '9px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {t('cancel')}
            </button>
            <button type="button" disabled={saving} onClick={save} style={{ padding: '9px 18px', borderRadius: 'var(--radius-md)', border: 'none', background: color, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? t('fe_saving') : t('save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Entry way 3: pick a money-out line off a bank statement ──────────────────────────────────────
// Only the statements this login may open, and only lines no live expense row has taken already —
// the server decides both, so this list is simply what came back.
// `category` is the sub-category whose list the picker was opened over, '' on a class's home and on
// Revenue, which has none (report 079721fa: "I cannot choose a document from the bank
// statement under Employee anymore"). The line picked is filed under it, so it appears on the list
// standing behind this pop-up; with no sub-category open the row arrives unfiled and waits on its
// class's home, which is where an uploaded PDF still goes too.
function BankPicker({ kind, category, onClose, onPicked }: { kind: ExpenseKind; category: string; onClose: () => void; onPicked: () => void }) {
  const { t } = useTranslation();
  const color = clsColor(kind);
  const [rows, setRows] = useState<ExpenseBankLine[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  const load = async (query: string) => {
    setLoading(true); setErr('');
    try { const r = await api.firmExpenseBankLines(kind, query); setRows(r.rows || []); }
    catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Search as you type, as every other list does (300ms).
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const tm = setTimeout(() => { void load(q); }, 300);
    return () => clearTimeout(tm);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (line: ExpenseBankLine) => {
    setBusy(line.id); setErr('');
    try { await api.firmExpenseFromBank(kind, line.id, category); onPicked(); onClose(); }
    catch (e: any) { setErr(e.message); setBusy(''); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 640, maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('fe_bank_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6, marginBottom: 12 }}>{t('fe_bank_hint')}</div>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <i className="ti ti-search" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 14.5, color }} aria-hidden />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('fe_bank_search')} className="mf-ph-inherit"
            style={{ width: '100%', padding: '7px 16px 7px 36px', fontSize: 13.5, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill(kind, false), background: lighten(pageTint(kind)!, -1) }} />
        </div>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 10 }}>{err}</div>}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: CARD_GAP }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><i className="ti ti-loader" style={{ fontSize: 22, color: 'var(--text-tertiary)' }} aria-hidden /></div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, fontSize: 13, color: 'var(--text-tertiary)' }}>{t('fe_bank_empty')}</div>
          ) : rows.map(line => {
            // The name the statement gives, with the statement's own description under it. A line that
            // names nobody shows the description as its name, and not twice.
            const who = displayClientName(line.counter_company || line.client_name);
            return (
              <button key={line.id} type="button" disabled={!!busy} onClick={() => pick(line)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', padding: '10px 12px', borderRadius: CARD_R, border: clsLine(kind), background: clsRowBg(kind), cursor: busy ? 'wait' : 'pointer' }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', width: 84, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{fmtDate(line.txn_date)}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-primary)' }}>
                  {who || line.description || '—'}
                  {!!who && !!line.description && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{line.description}</span>}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{currencySymbol()}{money(line.debit)}</span>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" onClick={onClose} style={{ padding: '9px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('cancel')}</button>
        </div>
      </div>
    </div>
  );
}

// ── The page, filling its flex parent: the standalone /m/ page and the Dashboard tab share it ────
// `doc` / `onDoc` (report 790f8217): the document bubble the address has open, '' for none, and the way to change it,
// '' being back to the class's own list. `docOpenId` is the address's ?open=, one document's form; `docBadge` a
// bubble's red count, which only the Dashboard has. `docsOnly`: this login reaches the page through `peak` and no
// class at all. `showApprovals`: the Approvals bubble is drawn right now, which only the Dashboard ever says.
export function ExpenseView({ kind, doc = '', onDoc, docOpenId, docBadge, docsOnly = false, showApprovals = false }: {
  kind?: ExpenseKind;
  doc?: RevenuePage | '';
  onDoc?: (d: RevenuePage | '') => void;
  docOpenId?: string;
  docBadge?: (d: RevenuePage) => React.ReactNode;
  docsOnly?: boolean;
  showApprovals?: boolean;
}) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  // The five classes are five separate permissions, and the server checks the one each request names
  // (routes/firmExpenses.ts). The bar draws all five whatever this login holds — Calem asked for five,
  // visible all the time — but the page OPENS on a class this login may read, so nobody lands on a
  // refusal. `kind` is the class an address asked for (/m/<class>); the Dashboard's tab passes none, and
  // either way the first readable class is the fallback.
  const mayRead = (k: string) => me?.role === 'admin' || !!me?.modules?.includes(k);
  // ── Who gets what under Revenue (report 790f8217) ──
  // The three documents are `peak`'s, as their tabs always were, and Revenue's own list is `income`'s. So income
  // without peak sees no row of bubbles at all. Peak with some class but not income has no list to come back to, so
  // Revenue opens its first document and never the list the server would refuse. Peak with no class at all (docsOnly)
  // sees the Revenue bubble alone, opened on a document, so the other four never make a refused call (those page the
  // admins).
  const mayDocs = mayRead('peak');
  const revenueHome = !docsOnly && mayRead(REVENUE_KIND);
  const { badgeOnly } = useBadgeOnly();   // a document's drill-down strip leads its list (the row's gap, below)
  const [picked, setPicked] = useState<ExpenseKind>(
    () => (kind && mayRead(kind) ? kind : (CLASSES.find(c => mayRead(c.key)) || CLASSES[0]).key));
  // A document is open under Revenue, whichever class was picked before the address asked for it.
  const open: ExpenseKind = doc ? REVENUE_KIND : picked;
  // Everything on the page is the open class's colour, and repaints with it when a bubble is clicked.
  const color = clsColor(open);
  const rowBg = clsRowBg(open);
  const cardLine = clsLine(open);
  const [rows, setRows] = useState<FirmExpense[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getPageSize());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  // The open sub-category of Expenses (report 35e85aca), Assets or Liabilities (report 21fa9aba) or Equity (report
  // dc2a3df6). '' is that class's HOME, which shows only the rows nobody has filed yet — so an uploaded or imported
  // batch waits there, and a row leaves the moment one of its bubbles is picked for it on its form. Revenue has none.
  const [cat, setCat] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<FirmExpense | null>(null);
  const [showBank, setShowBank] = useState(false);
  const [reading, setReading] = useState(false);
  // The day the firm's financial year starts, as the list answers with it (report 079721fa): the
  // Period box on a staff cost counts its quarters, halves and year from it, and Jan 1 — the
  // default an admin keeps in the system's own settings — makes them the calendar ones. It comes
  // with the list because the setting itself is admins-only and an ordinary filer must be able to
  // open this page.
  const [fy, setFy] = useState<FyStart>(FY_CALENDAR);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // The Description, Employee and Period columns, between Seller and Payment Date, while the Employee
  // sub-category is the open bubble and only then (report 079721fa) — so an ordinary supplier bill is
  // never squeezed by three cells it has nothing to put in. COLS is what the full-width rows span.
  const staffCols = open === EXPENSE_KIND && cat === STAFF_CATEGORY;
  const COLS = staffCols ? 9 : 6;

  // Search as you type (300ms), skipping the run the first paint would make — and the one a class change
  // makes below, which loads the new class's first page itself.
  const qInit = useRef(true);

  const load = async (p = page, size = pageSize, query = q, category = cat) => {
    setLoading(true); setErr('');
    try {
      // Only a class with sub-categories names one, and naming an EMPTY one is what asks for its home
      // list — the rows still waiting to be filed. Revenue asks for the whole class as before.
      const res = await api.firmExpenses(open, {
        page: p, limit: size, q: query, ...(categoriesOf(open).length ? { category } : {}),
      });
      setRows(res.rows || []); setTotal(res.total || 0); setPage(res.page || 1);
      // The firm's financial year rides with every list; the same answer is kept as the same object,
      // so a reload repaints nothing it need not.
      const fyM = Number(res.fy?.month), fyD = Number(res.fy?.day);
      if (fyM >= 1 && fyM <= 12 && fyD >= 1 && fyD <= 31) {
        setFy(cur => (cur.month === fyM && cur.day === fyD ? cur : { month: fyM, day: fyD }));
      }
    } catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  };
  // Clicking a bubble shows that class's list: its first page, with the search box emptied and no
  // sub-category open, so every class but Revenue opens on its home list of rows nobody has filed yet.
  // A document's list loads itself, so nothing is asked for here while one is open — that is also what keeps a login
  // with no `income` from ever being refused Revenue's list — and coming back from one loads the class afresh.
  useEffect(() => {
    if (doc) return;
    setQ(''); setCat(''); qInit.current = true; void load(1, pageSize, '', '');
  }, [open, !doc]); // eslint-disable-line react-hooks/exhaustive-deps
  // One of the sub-category bubbles, or the open one clicked again, which goes back to its class's home.
  // It loads the first page itself, exactly as a class change does, rather than waiting on an effect.
  const showCat = (next: string) => {
    setCat(next);
    if (q) { setQ(''); qInit.current = true; }
    void load(1, pageSize, '', next);
  };
  // A class bubble: a different class opens it (the effect above loads it), and the class already open
  // goes back to its own home list — which for every class but Revenue means closing the open sub-category,
  // and from a document means closing that. Revenue with no list for this login goes to its first document instead
  // (see revenueHome).
  const clickClass = (k: ExpenseKind) => {
    if (k === REVENUE_KIND && mayDocs && !revenueHome) { if (!doc) onDoc?.(REVENUE_DOCS[0].key); return; }
    setPicked(k);
    if (doc) onDoc?.('');
    else if (k === open && cat) showCat('');
  };
  // One of the row's bubbles. The open one clicked again goes back to Revenue's own list where this login has one;
  // where it has none it stays open, and the click only drops a drill-down, as a top-menu tab's click always did.
  const clickDoc = (d: RevenuePage) => (d === doc && revenueHome ? clickClass(REVENUE_KIND) : onDoc?.(d));
  useEffect(() => {
    if (qInit.current) { qInit.current = false; return; }
    const tm = setTimeout(() => { void load(1, pageSize, q); }, 300);
    return () => clearTimeout(tm);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Entry way 2: a supplier's PDF. The document is stored and read into a new Draft; whoever opens
  // the row then checks the four boxes against the document itself.
  const pickPdf = async (file: File | undefined) => {
    if (!file) return;
    setReading(true); setErr('');
    try {
      const r = await api.firmExpenseFromPdf(open, file);
      if (r.readError) setErr(t('fe_pdf_unread', { msg: r.readError }));
      await load(1, pageSize, q);
    } catch (e: any) { setErr(e.message); }
    finally { setReading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  // 10 → 5px under the heading text (report c151d269: "Bring the heading text 5 pixels closer to the grey line
  // below. Do this for every page." — see Active Jobs' th). thEdge hangs off this cell's top and bottom, so the
  // fill either side shortens with the band.
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 9px 5px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-surface)' };
  const thEdge: React.CSSProperties = { position: 'absolute', top: 0, bottom: -1, width: CARD_INSET, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-strong)' };
  const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '9px 9px', borderTop: cardLine, borderBottom: cardLine, whiteSpace: 'nowrap' };
  const edgeL: React.CSSProperties = { borderLeft: cardLine, borderTopLeftRadius: CARD_R, borderBottomLeftRadius: CARD_R };
  const edgeR: React.CSSProperties = { borderRight: cardLine, borderTopRightRadius: CARD_R, borderBottomRightRadius: CARD_R };
  // The page above each card, and a strip more under the last one. See-through by a colour set on the
  // row, not by having none: global.css's zebra would otherwise shade an even gap row.
  const cardGap = (key: string) => (
    <tr key={key} aria-hidden="true" style={{ background: 'transparent' }}><td colSpan={COLS} style={{ padding: 0, height: CARD_GAP, border: 'none' }} /></tr>
  );
  // A toolbar button in the open class's own colour, dressed as an unselected filter pill (menuPill). It stands on the
  // class bar (report 67a982e2) and is the bar's BAR_PILL, the same size as the five class bubbles beside it.
  const toolBtn = (icon: string, label: string, onClick: () => void, busy = false): React.ReactElement => (
    <button type="button" onClick={onClick} disabled={busy}
      style={{ ...BAR_PILL, display: 'flex', alignItems: 'center', gap: 6, ...menuPill(open, false), cursor: busy ? 'wait' : 'pointer' }}>
      <i className={`ti ti-${busy ? 'loader' : icon}`} style={{ fontSize: 14 }} aria-hidden />{label}
    </button>
  );

  const openNew = () => { setEditing(null); setShowForm(true); };

  // The bubbles of Revenue's row: the three documents, and Approvals behind them while Portal says it is drawn.
  const revenueRow: { key: RevenuePage; label: string }[] = showApprovals
    ? [...REVENUE_DOCS, APPROVALS_PAGE] : REVENUE_DOCS;
  // The open document bubble's list, in the page body in place of the class's table and its paging.
  const docList = doc === 'quotation' ? <QuotationList initialOpen={docOpenId} />
    : doc === 'invoice' ? <InvoiceList initialOpen={docOpenId} />
      : doc === 'receipt' ? <ReceiptList initialOpen={docOpenId} />
        : doc === 'outbox' ? <OutboxView group="docs" /> : null;
  // The two stitched bubbles of Assets or Liabilities, side by side in the middle of the page (report 21fa9aba; the
  // comment over the sub-category row below has the rest). The two wrap onto a centred line each on a window too narrow
  // for both. The stitched line is the open class's own colour. Its ends are round like the pills inside it (report
  // 18362c07: "more circular (not rectangular), like the bubbles inside the stitched one"), the pills' own 999 where it
  // had the cards' CARD_R, and its sides go from 10px to 14px so the name and the end pills clear those round ends.
  // Only those ends are drawn (report 0a97c655: "Change the stitch border to end brackets only for assets and
  // liabilities"). The top and bottom edges keep their 1px but go transparent, so the box, its padding and the row's
  // width stay as they were and the brackets stay one even width; borderTop: 'none' would taper them and take 2px off
  // the height. A browser gives each edge half of a round corner, so a bracket is the middle half of its round end:
  // about 28px tall and 6px deep on the 40px box (worked out, not measured), shorter than the curves in that report's
  // screenshot, and 9px clear of the name and the end pills, which stand 15px in from the box's edge.
  const groupRow = GROUPS_OF[open] && (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', rowGap: BAR_GAP, columnGap: ASSET_GROUP_GAP }}>
      {GROUPS_OF[open].map(g => (
        <div key={g.key} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: BAR_GAP, padding: '6px 14px', borderRadius: 999, borderStyle: 'dashed', borderWidth: 1, borderColor: `transparent ${color}`, background: 'transparent' }}>
          {!g.captionEnd && <div style={BAR_CAPTION}>{t(g.label)}</div>}
          {g.cats.map(c => (
            <button key={c.key} type="button" onClick={() => showCat(c.key === cat ? '' : c.key)}
              style={{ ...BAR_PILL, ...g.pill(c.key === cat) }}>
              {t(c.label)}
            </button>
          ))}
          {g.captionEnd && <div style={BAR_CAPTION}>{t(g.label)}</div>}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      {/* ── The five account classes, across the page (Calem, report eb29e199) ──
          "do a second bubble bar running horizontally across the page that includes five categories in this order:
          Income/revenue … Expenses … Assets … Liabilities … Equity" — and it stays on screen the whole time you
          work: "the 5-menu bubble can be visible all the time under the Accounting page". All five are drawn to
          anyone who may open the page, whichever of them this login holds — except a login here through `peak`
          alone, which sees Revenue and nothing else (report 790f8217, docsOnly).
          Each bubble is dressed as a filter pill of the box its colour came from (menuPill, ACCOUNTING_BOX_OF):
          the open class filled with its own colour and its word in white, the other four in that colour's pale
          wash with its line — blue, red, green, orange and pink, the first and third swapped at report 790f8217.
          The bar is the page's ONLY toolbar bubble (pageToolbar, report 76a2c198): it stands in the page's one 15px
          gutter with the same 7px of page above and below it. A second strip used to be stacked under it for the
          list's own toolbar (pageToolbarUnder); report 35e85aca emptied it — see the sub-category row below.
          Left to right: the "Categories" caption, the five, a clear, the Upload PDF / from Bank Statement pair, a
          second clear, the refresh button and the search box. Every bubble is a BAR_PILL at its own width with the
          plain BAR_GAP between neighbours, as on every other top bar (report 0d7b170d — the spec is at BAR_PILL). The
          flex shares that used to size each of the five and each gap between them, and that earlier reports nudged a
          few pixels at a time, are gone; only the two clears grow. On a window too narrow for the lot the bar runs
          past the page's right edge — nothing on it shrinks. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: BAR_GAP, padding: '8px 16px', ...pageToolbar(CARD_INSET), background: 'var(--c-gray-50)' }}>
        {/* The word that says what the five are (report 9a24f8c1). It is the bar's own type, not a pill: no border,
            no fill, nothing happens when it is clicked. It stands outside the run, so the bar's own BAR_GAP is
            what sits between it and the first bubble. */}
        <div style={BAR_CAPTION}>{t('fe_cls_label')}</div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: BAR_GAP }}>
          {/* The five, each at its own word's width. The row below Expenses or Revenue is centred on the page, not
              under its bubble (see that row). */}
          {CLASSES.filter(c => !docsOnly || c.key === REVENUE_KIND).map(c => (
            <button key={c.key} type="button" onClick={() => clickClass(c.key)} title={c.tip ? t(c.tip) : undefined}
              style={{ ...BAR_PILL, ...menuPill(c.key, c.key === open) }}>
              {t(c.label)}
            </button>
          ))}
          {/* The FIRST of the two clears (report 94a7519b). It and the one after the pair grow alike out of the bar's
              free space, so the five pack against the bar's left edge, the search box stands at its right and the
              pair is centred between "Equities" and the refresh button. */}
          <div aria-hidden style={{ flex: 1 }} />
          {/* ── The two other ways in (report 67a982e2), now in the middle of the bar (report 94a7519b) ──
              "On the accounting tab move the bubbles as shown below. Change the wording from 'Upload Supplier PDF'
              to 'Upload PDF' and 'From the Bank Statement' to 'from Bank Statement'." They stood at the left of the
              strip below, where a page toolbar usually keeps its ways in, and that report's arrow ended here — on
              the class bar — so they left the strip below with the refresh button alone. The shorter words are that
              report's too (fe_from_pdf / fe_from_bank), which is also what keeps them from spending much of the bar.
              They sat at the bar's far right, the last two items before the search box, until report 94a7519b:
              "Move the green upload / from bank statement (green bubbles) to the middle of the menu bar. with even
              spacing between left and right menu options (Equity) and (Search tool)." So they stand between the two
              equal clears instead, flex: 'none', each at its own word, with the run's BAR_GAP between them.
              Both pills are still the open class's colour and repaint when a bubble is clicked, and the hidden file
              box the PDF pill opens came up with them.
              While a document is open (report 790f8217) these two, the refresh button and the search box stand aside:
              they all work on the class's list, and the document's list has its own search, refresh and "+". Approvals
              counts as a document here, with its own refresh and nothing to add (report 790f8217, 2nd opening). */}
          {!doc && toolBtn('file-upload', t('fe_from_pdf'), () => fileRef.current?.click(), reading)}
          {!doc && toolBtn('building-bank', t('fe_from_bank'), () => setShowBank(true))}
          <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
            onChange={e => pickPdf(e.target.files?.[0])} />
          {/* The SECOND clear: the same grow as the one in front of the pair, so what stands between "from Bank
              Statement" and the refresh button reads exactly as wide as what stands between "Equities" and "Upload PDF". */}
          <div aria-hidden style={{ flex: 1 }} />
          {/* The refresh button, up from the grey strip that used to stand under this bar (report 35e85aca: "Also
              bring the refresh icon up into the next bubble above, behind the search"). It is the same 32px button
              in the same dressing, clicked for the same reload — only the bar it stands on is new, and with it gone
              the strip below held nothing at all and is deleted. It takes flex: 'none' with the run's BAR_GAP
              between it and the search box, and its -3px margins keep it inside the bar's height exactly as the
              search box's do, so the bar is no taller than it was. */}
          {!doc && (
            <button onClick={() => load()} title={t('refresh')}
              style={{ flex: 'none', width: 32, height: 32, margin: '-3px 0', borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <i className="ti ti-refresh" style={{ fontSize: 15 }} aria-hidden />
            </button>
          )}
          {/* Filing's search box, with the class's name inside it (report 48c2197c). It stood in the toolbar below
              until report eb29e199 put it here at the bar's far right; the page has the one search bubble either way.
              Its 220px, its place at the bar's right-hand end and the negative margin that keeps the bar the height
              it always was are all untouched.
              Its colour is the page's top-menu red and no longer the open class's (report 7dab1f21, PAGE_SEARCH) —
              the line, the wash, the words and the magnifier together, so it no longer turns pink behind Equities.
              The words inside it still name the open class: the bar word (fe_cls_<class>) since report 94a7519b
              asked for the five as plurals, where it used to ask for the class's MODULE name and so was the one
              thing on this bar still reading "Income / Revenue".
              With a document open it is hidden rather than dropped, so the bar keeps its height and the page under it
              does not jump a pixel between the class's list and the document's. */}
          <div style={{ position: 'relative', flex: 'none', margin: '-3px 0', visibility: doc ? 'hidden' : undefined }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 14.5, color: PAGE_COLOR }} aria-hidden />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('fe_search', { page: t(`fe_cls_${open}`) })} className="mf-ph-inherit"
              style={{ width: 220, padding: '7px 16px 7px 36px', fontSize: 13.5, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...PAGE_SEARCH }} />
          </div>
        </div>
      </div>

      {/* ── The Expenses class's 15 sub-categories (Calem, report 35e85aca) ──
          "When you click on Expenses, directly below it add the following 15 bubbles, also in the same red color
          as Expenses, but remove the outer gray bubble." So this row is drawn for the Expenses class, directly
          under the class bar, with nothing behind it: no grey band, no bubble, no line — the page's own white, the
          page's 15px gutter, and the bubbles themselves. They are the class bar's own bubbles in every other way:
          the same BAR_PILL with the same BAR_GAP between them (report 0d7b170d), and the same open/unopen fills,
          both taken from the Expenses box (menuPill(open, …)), so all 15 are the red of the "Expenses"
          bubble above them whichever one is open. One shared row that WRAPS rather than clipping — fifteen words will
          not sit on one line on a narrow window, and a sub-category must never be unreachable.
          Clicking one shows that sub-category and nothing else; clicking the open one again, or the "Expenses" bubble
          above, goes back to the home list of rows nobody has filed yet.
          The grey strip that used to stand here is GONE with this report: the search box left it at the 3rd opening
          of report eb29e199, Upload PDF and from Bank Statement at report 67a982e2, and the refresh button has just
          gone up onto the bar too ("bring the refresh icon up into the next bubble above, behind the search"), so it
          held nothing. The class bar's own 7px of page under it was the gap above this row, and this row's own
          7px the gap under it — the same one gap the page has everywhere, whether this row is drawn or not.
          3rd opening: "Spread the categories across the page and add 15 pixels of space between the bubble above and
          the categories below." The 15px of that is CAT_ROW_DROP, this row's own top margin, on top of the 7px the
          bar above already leaves: 22px of page between the bar and the bubbles. The spreading half of it went with
          report 775719f3, which centred the fifteen on the page instead. Report 0d7b170d then asked for them "centered
          underneath the top accounting category", and report 790f8217's 3rd opening ("center these bubbles", on a
          screenshot of this row) supersedes that: they are centred on the page again. The geometry decided it. The
          fifteen are about as wide as the page and the "Expenses" bubble stands far to its left, so under the bubble
          they could only stop against the left gutter, off-centre, unless wrapped into a block of four or five lines.
          The outer box is the row's whole width, gutter to gutter, and centres the inner run that holds the fifteen. On
          a window too narrow for one line the run wraps, fills the box and each wrapped line centres its own bubbles.
          Revenue's three documents (report 790f8217) stand in this same box, centred the same way (2nd opening:
          "Center the quotation, invoice, and receipt bubbles"), with Approvals after them while it is drawn
          (revenueRow). They are what were the top menu's buttons, so each keeps what its button had: the
          blue box's colours (menuPill), the right-click into a new tab with its hover words (report ab347131), and the
          red count with its pop-up list (docBadge, position relative for it to hang from). A document's list leads with
          its own toolbar bubble, whose TOOLBAR_GAP above it is the gap under this row, so the row keeps none of its own
          there — unless the red drill-down strip, which has no gap above it, comes first.
          The Assets class's ten (report 21fa9aba, ASSET_GROUPS) stand in this same box too, with the same drop and
          gutters, centred the same way (2nd opening: "Bring all assets back to back in the center of the page"): Non
          Current's stitched bubble and Current's side by side, ASSET_GROUP_GAP apart (report 54ffdf28's 2nd opening:
          "create more separation (15px) between non-current and current", 25px since report 18362c07), or a BAR_GAP
          apart once each has a line of its own on a narrow window. A stitched bubble is a see-through box drawn only as
          a dashed green bracket at each round end (report 0a97c655: "end brackets only"; see groupRow), its BAR_PILLs
          at BAR_GAP and its name in the "Categories" caption's type, in front of them on Non Current and after them on
          Current, so the spec above still holds for every bubble in it.
          Clicking one shows its rows; clicking it again, or "Assets" above, comes home, as on Expenses.
          The Liabilities class's eleven (report 21fa9aba's 3rd opening, LIABILITY_GROUPS: "but again back-to-back and
          centered on the screen") stand in this box the same way, with orange lines and pills where Assets has
          green. The 3rd opening's words came to about 1,520px against the 1,506px this box has on the reporter's 1536px
          page, so each group took a line of its own. The 4th opening's shorter words ("fit them on one line, centered")
          come to about 1,245px, worked out from the pills in that screenshot, not measured on a page: about 1,220px at
          the gap and sides it shows, plus report 18362c07's ASSET_GROUP_GAP of 25 (10 more) and 14px stitched sides (16
          more). A window under about 1,275px wide still gives each group its own centred line.
          The Equity class's five (report dc2a3df6, EQUITY_CATEGORIES: "also centered as per every other subcategory")
          stand in this box as the Expenses fifteen do: one plain centred run with nothing round it, the same BAR_PILL
          and BAR_GAP, all in the pink of the "Equities" bubble above them. Clicking one shows its rows; clicking it
          again, or that bubble, comes home. */}
      {(categoriesOf(open).length > 0 || (open === REVENUE_KIND && mayDocs)) && (
        <div style={{ display: 'flex', justifyContent: 'center', flexShrink: 0, margin: `${CAT_ROW_DROP}px ${CARD_INSET}px ${doc && !badgeOnly ? 0 : CARD_GAP}px` }}>
          {groupRow || <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: BAR_GAP }}>
            {PLAIN_CATS_OF[open] ? PLAIN_CATS_OF[open].map(c => (
              <button key={c.key} type="button" onClick={() => showCat(c.key === cat ? '' : c.key)}
                style={{ ...BAR_PILL, ...menuPill(open, c.key === cat) }}>
                {t(c.label)}
              </button>
            )) : revenueRow.map(d => (
              <button key={d.key} type="button" onClick={() => clickDoc(d.key)} onContextMenu={openInNewTab(pageHref(d.key))}
                title={t('nt_open', { page: t(d.label) })} style={{ ...BAR_PILL, position: 'relative', ...menuPill(d.key, d.key === doc) }}>
                {t(d.label)}
                {docBadge?.(d.key)}
              </button>
            ))}
          </div>}
        </div>
      )}

      {docList || <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-surface)' }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
        ) : categoriesOf(open).length > 0 && !cat && !q && !err && total === 0 ? (
          /* The Expenses home with nothing left waiting (Calem, report 35e85aca): "Once the user has
             successfully removed all uncategorized expenses, bring up a message on the main screen" —
             in place of the table, so there are no column headings over an empty list either. The words
             are the fe_cat_all_done row in src/i18n/index.ts, re-worded to his own sentence at report
             079721fa, and they are not repeated here. Only on the home list itself: a sub-category with
             no rows, and a search that finds none, still say so in the page's ordinary words (fe_empty).
             It reads the TOTAL the server counted and not this page's rows, so filing the last row of
             page 2 cannot congratulate anyone while page 1 is still full. The Assets, Liabilities and Equity homes
             say the same when they have nothing left waiting (reports 21fa9aba, dc2a3df6). */
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' }}>
            {t('fe_cat_all_done')}
          </div>
        ) : (
          <table style={{ width: '100%', minWidth: TABLE_MIN + (staffCols ? STAFF_DESC_W + STAFF_EMP_W + STAFF_PER_W : 0) + 2 * CARD_INSET, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', padding: `0 ${CARD_INSET}px` }}>
            {/* Seller is the one column with no width, so it takes whatever the table has over the rest —
                including the 190px the Doc. No. column held until report 079721fa, which is the whole of
                that re-sharing. Under the Employee bubble it shares with the three staff columns instead. */}
            <colgroup>
              <col />{/* seller — the rest */}
              {staffCols && <><col style={{ width: STAFF_DESC_W }} />{/* description */}<col style={{ width: STAFF_EMP_W }} />{/* employee */}<col style={{ width: STAFF_PER_W }} />{/* period */}</>}
              <col style={{ width: 110 }} />{/* issue date */}
              <col style={{ width: 130 }} />{/* net amount */}
              <col style={{ width: 110 }} />{/* status */}
              <col style={{ width: 170 }} />{/* activity */}
              <col style={{ width: 26 }} />{/* chevron */}
            </colgroup>
            <thead><tr>
              {/* Seller leads the row now, so it carries the heading band's left-hand edge strip. */}
              <th style={th}><span aria-hidden style={{ ...thEdge, left: -CARD_INSET }} />{t('fe_col_seller')}</th>
              {/* Period stands between Employee and Payment Date, the place Calem's own picture marks
                  it (report 079721fa) — the third of the three columns that report asked for. */}
              {staffCols && <><th style={th}>{t('fe_col_description')}</th><th style={th}>{t('fe_col_employee')}</th><th style={th}>{t('fe_col_period')}</th></>}
              {/* The same column, named for what the date is on a staff cost — "Change issue date to
                  payment date in the column below" (report 079721fa). Under the Employee bubble only:
                  every other class, and the Expenses home itself, still reads Issue Date, and the
                  stored date is the one field whichever word stands over it. */}
              <th style={th}>{t(staffCols ? 'fe_col_payment_date' : 'fe_col_issue_date')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('fe_col_net_amount')}</th>
              <th style={th}>{t('fe_col_status')}</th>
              <th style={th}>{t('fe_col_activity')}</th>
              <th style={th}><span aria-hidden style={{ ...thEdge, right: -CARD_INSET }} /></th>
            </tr></thead>
            <tbody>
              {rows.flatMap(r => {
                const st = statusOf(r.status);
                return [
                  cardGap(`gap-${r.id}`),
                  <tr key={r.id} onClick={() => { setEditing(r); setShowForm(true); }} style={{ cursor: 'pointer', background: rowBg }}
                    onMouseEnter={ev => (ev.currentTarget.style.background = lighten(rowBg, -3))}
                    onMouseLeave={ev => (ev.currentTarget.style.background = rowBg)}>
                    {/* Seller leads the row now that Doc. No. is gone (report 079721fa), so the mark
                        saying a supplier document is attached rides with it. Tidied as every list shows
                        a party (report 101b5c69): titles and a company's legal form off. */}
                    <td style={{ ...td, ...edgeL, whiteSpace: 'normal' }}>
                      {!!r.file_name && <i className="ti ti-paperclip" title={r.file_name} style={{ fontSize: 13, color, marginRight: 5 }} aria-hidden />}
                      {displayClientName(r.seller) || '—'}
                    </td>
                    {/* The staff-cost detail, under the Employee bubble only. The word comes from the
                        slug stored, so it reads in whichever language the page is in. Period says
                        exactly what the pop-up's two boxes were answered with (periodText above), so
                        the month, the pair of dates or the payment date can be read off the list. */}
                    {staffCols && <>
                      <td style={td}>{r.staff_description ? t(`fe_desc_${r.staff_description}`) : '—'}</td>
                      <td style={{ ...td, whiteSpace: 'normal' }}>{r.staff_employee_name || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'normal' }}>{periodText(r, fy, t)}</td>
                    </>}
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtDate(r.issue_date)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{currencySymbol()}{money(r.net_amount)}</td>
                    {/* The status as a coloured word — the reporter's four, in the order they gave. */}
                    <td style={{ ...td, fontWeight: 700, color: STATUS_COLOR[st] }}>{t(STATUS_LABEL[st])}</td>
                    {/* The hand, the branch they work in and the time. The branch is Calem's at report
                        079721fa — "activity should be recorded branch by branch as we roll out this SLF
                        system to other branches. They will be able to use this accounting module, but for
                        their branch" — and it is read off the branch list an admin keeps in Admin →
                        Contacts, against the office card carrying that login's mailbox. A member of staff
                        whose card names no branch simply has no line, and nothing here names an office. */}
                    <td style={{ ...td, color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>
                      <div style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: 12 }}>{r.updated_by_name || r.created_by_name || '—'}</div>
                      {!!r.activity_branch && <div style={{ fontSize: 11 }}>{r.activity_branch}</div>}
                      <div style={{ fontSize: 11 }}>{fmtDateTime(r.updated_at || r.created_at)}</div>
                    </td>
                    <td style={{ ...td, ...edgeR, color: 'var(--text-tertiary)' }}><i className="ti ti-chevron-right" aria-hidden /></td>
                  </tr>,
                ];
              })}
              {rows.length > 0 && cardGap('end')}
              {rows.length === 0 && (
                <tr><td style={{ ...td, borderTop: 'none', borderBottom: 'none', textAlign: 'center', color: 'var(--text-tertiary)', padding: 40 }} colSpan={COLS}>{t('fe_empty')}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>}

      {!doc && <Pagination page={page} total={total} limit={pageSize} onPage={p => load(p)} onLimit={n => { setPageSize(n); load(1, n); }} />}

      {/* The round "+" at the bottom right of the page, in the page's top-menu colour (Bugify #7568b3b5) — the
          "Accounting" button's red whichever bubble is open, as every other page's + wears its own button's
          colour (report 7dab1f21, PAGE_COLOR). Its tooltip still names the open class, which is what it adds.
          A document's list draws its own (report 790f8217), so this one stands aside for it. */}
      {!doc && <Fab onClick={openNew} title={t('fe_add', { page: t(`mod_${open}`) })} color={PAGE_COLOR} />}

      {showForm && <ExpenseForm kind={open} row={editing} fy={fy} onClose={() => setShowForm(false)} onSaved={() => load(page)} />}
      {/* The open sub-category travels with the pick (report 079721fa), so the line lands on the list the
          picker was opened over and not on its class's home behind it. The reload below asks for that same
          list — load()'s own default sub-category — so the new row is on screen the moment the pop-up shuts. */}
      {showBank && <BankPicker kind={open} category={categoriesOf(open).length ? cat : ''} onClose={() => setShowBank(false)} onPicked={() => load(1, pageSize, q)} />}
    </div>
  );
}

// ── Standalone module page (/m/expense, /m/asset …): brand header + the shared view ──────────────
// One page per class address, all of them the Accounting page: the header reads "Accounting" and the
// bar below it opens on the class the address names (report eb29e199). A document bubble goes to that
// document's own address, the Dashboard's tab (report 790f8217): it has no page of its own to open here.
export default function Expense({ kind }: { kind: ExpenseKind }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_accounting')}</div>
      </header>
      <ExpenseView key={kind} kind={kind} onDoc={d => { if (d) navigate(pageHref(d)); }} />
    </div>
  );
}
